import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { X, ChevronLeft, ChevronRight } from 'lucide-react';

interface ImageViewerProps {
  images: Array<{
    id: string;
    imageUrl: string;
    alt?: string;
  }>;
  currentIndex: number | null;
  onClose: () => void;
  onNavigate?: (direction: 'prev' | 'next') => void;
  showInfo?: boolean;
  className?: string;
}

/**
 * ImageViewer - Common image viewer component with loading states
 * Used across the app for consistent image viewing experience
 */
export const ImageViewer: React.FC<ImageViewerProps> = ({
  images,
  currentIndex,
  onClose,
  onNavigate,
  showInfo = true,
  className = ''
}) => {
  const [imageLoading, setImageLoading] = useState(true); // Start with loading true

  const currentImage = currentIndex !== null ? images[currentIndex] : null;

  // Reset loading state when currentIndex changes
  useEffect(() => {
    if (currentIndex !== null) {
      setImageLoading(true);
    }
  }, [currentIndex]);

  const handleNavigate = (direction: 'prev' | 'next') => {
    if (!onNavigate) return;
    setImageLoading(true);
    onNavigate(direction);
  };

  const handleImageLoad = () => {
    setImageLoading(false);
  };

  if (!currentImage) return null;

  return (
    <AnimatePresence>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className={`fixed inset-0 z-50 bg-black bg-opacity-95 flex items-center justify-center p-4 ${className}`}
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="relative max-w-4xl max-h-full flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {/* Image Container */}
          <div className="relative flex-1 flex items-center justify-center">
            {/* Loading Animation - Spinner */}
            {imageLoading && (
              <div className="absolute inset-0 flex items-center justify-center bg-black bg-opacity-50 rounded-lg">
                <div className="w-8 h-8 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              </div>
            )}
            
            <img
              src={currentImage.imageUrl}
              alt={currentImage.alt || `Image ${currentIndex! + 1}`}
              className={`max-w-full max-h-[80vh] object-contain rounded-lg ${imageLoading ? 'opacity-0' : 'opacity-100'} transition-opacity duration-300`}
              onLoad={handleImageLoad}
            />
          </div>

          {/* Controls positioned below the image - Always rendered to prevent layout shifts */}
          <div className="flex flex-col items-center space-y-4 mt-4">
            {/* Navigation Controls */}
            <div className="flex items-center justify-center space-x-6">
              {/* Previous Button */}
              {onNavigate && images.length > 1 ? (
                <button
                  onClick={() => handleNavigate('prev')}
                  disabled={imageLoading}
                  className="w-12 h-12 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors shadow-lg"
                >
                  <ChevronLeft className="w-6 h-6 text-gray-700" />
                </button>
              ) : (
                <div className="w-12 h-12" /> // Placeholder to maintain layout
              )}
              
              {/* Image Info */}
              {showInfo && (
                <div className="bg-black bg-opacity-60 text-white px-4 py-2 rounded-lg">
                  <p className="text-sm text-gray-300">
                    Image {currentIndex! + 1} of {images.length}
                  </p>
                </div>
              )}
              
              {/* Next Button */}
              {onNavigate && images.length > 1 ? (
                <button
                  onClick={() => handleNavigate('next')}
                  disabled={imageLoading}
                  className="w-12 h-12 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors shadow-lg"
                >
                  <ChevronRight className="w-6 h-6 text-gray-700" />
                </button>
              ) : (
                <div className="w-12 h-12" /> // Placeholder to maintain layout
              )}
            </div>
            
            {/* Close Button */}
            <button
              onClick={onClose}
              disabled={imageLoading}
              className="w-10 h-10 bg-white hover:bg-gray-100 disabled:opacity-50 disabled:cursor-not-allowed rounded-full flex items-center justify-center transition-colors shadow-lg"
            >
              <X className="w-5 h-5 text-gray-700" />
            </button>
          </div>
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};